import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { AppProvider } from "@/context/AppContext";
import { Layout } from "@/components/Layout";
import { DailyBalanceGate } from "@/components/DailyBalanceGate";
import { Loader2 } from "lucide-react";

import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import POS from "@/pages/POS";
import Items from "@/pages/Items";
import Stock from "@/pages/Stock";
import Purchases from "@/pages/Purchases";
import Sales from "@/pages/Sales";
import Finance from "@/pages/Finance";
import Debts from "@/pages/Debts";
import Reports from "@/pages/Reports";
import Payroll from "@/pages/Payroll";
import Users from "@/pages/Users";
import AuditLog from "@/pages/AuditLog";
import Backup from "@/pages/Backup";
import Settings from "@/pages/Settings";

function Spinner() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-100">
      <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
    </div>
  );
}

function RoleOnly({ roles, children }) {
  const { user } = useAuth();
  return roles.includes(user.role) ? children : <Navigate to="/dashboard" replace />;
}

function PosRoute() {
  const { user, loading } = useAuth();
  if (loading) return <Spinner />;
  if (!user) return <Navigate to="/login" replace />;
  if (!["master", "admin", "kasir"].includes(user.role)) return <Navigate to="/dashboard" replace />;
  return <DailyBalanceGate><POS /></DailyBalanceGate>;
}

function AppRoute() {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <Spinner />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "kasir") return <Navigate to="/pos" replace />;
  return (
    <DailyBalanceGate>
      <Layout>
        <Routes>
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/items" element={<Items />} />
          <Route path="/stock" element={<Stock />} />
          <Route path="/purchases" element={<Purchases />} />
          <Route path="/sales" element={<RoleOnly roles={["master"]}><Sales /></RoleOnly>} />
          <Route path="/finance" element={<Finance />} />
          <Route path="/debts" element={<RoleOnly roles={["master", "admin"]}><Debts /></RoleOnly>} />
          <Route path="/payroll" element={<RoleOnly roles={["master", "admin"]}><Payroll /></RoleOnly>} />
          <Route path="/reports" element={<Reports />} />
          <Route path="/users" element={<Users />} />
          <Route path="/audit" element={<AuditLog />} />
          <Route path="/backup" element={<Backup />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </Layout>
    </DailyBalanceGate>
  );
}

function LoginRoute() {
  const { user, loading } = useAuth();
  if (loading) return <Spinner />;
  if (user) return <Navigate to={user.role === "kasir" ? "/pos" : "/dashboard"} replace />;
  return <Login />;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppProvider>
            <Routes>
              <Route path="/login" element={<LoginRoute />} />
              <Route path="/pos" element={<PosRoute />} />
              <Route path="/*" element={<AppRoute />} />
            </Routes>
            <Toaster position="top-right" richColors />
          </AppProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
